import { ChevronDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { cn } from '@/lib/utils'

import type { FilterOption } from './define'

export interface SelectFilterProps<T extends string> {
    label: string
    options: readonly FilterOption<T>[]
    value: T | ''
    onChange: (next: T | '') => void
    /**
     * Adds an explicit "no filter" entry with this label. Omit for a control where
     * one of the options is always in force, like a sort field.
     */
    anyLabel?: string
    searchable?: boolean
    disabled?: boolean
    className?: string
}

/**
 * Single-choice filter, shaped like `MultiSelectFilter` so a filter bar reads
 * consistently whether a field takes one value or several.
 */
export function SelectFilter<T extends string>({
    label,
    options,
    value,
    onChange,
    anyLabel,
    searchable = false,
    disabled = false,
    className
}: SelectFilterProps<T>) {
    const active = options.find(option => option.value === value)

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={disabled}
                    aria-label={`${label} filter`}
                    // Filter triggers sit shoulder to shoulder in the bar, so they are a
                    // cluster: they grow on a coarse pointer (and the bar widens its gap
                    // to match) rather than taking overlapping hit-slop.
                    className={cn('justify-between gap-1.5 pointer-coarse:h-10', className)}>
                    <span className={active ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
                    {active && (
                        <>
                            <Separator orientation="vertical" className="h-3.5" />
                            <span className="max-w-32 truncate text-foreground">{active.label}</span>
                        </>
                    )}
                    <ChevronDown aria-hidden className="size-3.5 text-subtle-foreground" />
                </Button>
            </PopoverTrigger>

            <PopoverContent align="start" className="w-56 p-0">
                <Command>
                    {searchable && <CommandInput placeholder={`Filter ${label.toLowerCase()}`} />}
                    <CommandList>
                        <CommandEmpty>No matches</CommandEmpty>
                        <CommandGroup>
                            {anyLabel && (
                                <CommandItem
                                    value={anyLabel}
                                    onSelect={() => onChange('')}
                                    role="option"
                                    aria-selected={value === ''}
                                    className={cn(value === '' && 'font-medium')}>
                                    {anyLabel}
                                </CommandItem>
                            )}
                            {options.map(option => (
                                <CommandItem
                                    key={option.value}
                                    value={option.label}
                                    onSelect={() => onChange(option.value)}
                                    role="option"
                                    aria-selected={option.value === value}>
                                    <div className="min-w-0 flex-1">
                                        <div className={cn('truncate', option.value === value && 'font-medium')}>
                                            {option.label}
                                        </div>
                                        {option.hint && (
                                            <div className="truncate text-xs text-muted-foreground">{option.hint}</div>
                                        )}
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
